import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import formIcon from 'assets/images/icons/form-grey.svg';
import sectionIcon from 'assets/images/icons/section-grey.svg';
import AlertModal from 'components/common-components/AlertModal';
import DateTimeField from 'components/form-components/DateTimeField';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import NestedSelect from 'components/form-components/NestedSelect';
import NumberField from 'components/form-components/NumberField';
import RatingStarField from 'components/form-components/RatingStarField';
import SignatureField from 'components/form-components/SignatureField';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import TextField from 'components/form-components/TextField';
import FormButton from 'components/form-components/FormButton';
import filePlusIcon from 'assets/images/icons/file-plus.svg';
import TextArea from 'components/form-components/TextArea';
import { isRTL } from 'config/config';

const AddSubmission = ({
  supervisorList,
  handleClose,
  onSubmitForm,
  values,
  loading,
  isNestedModal,
  formName,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(null);
  const [notifyViaSms, setNotifyViaSms] = useState({});
  const [fieldsNotes, setFieldsNotes] = useState({});
  const [openedNotesModal, setOpenedNotesModal] = useState(null);
  const [expandedNoteField, setExpandedNoteField] = useState(null);
  const [anyRequiredFieldUnfilled, setAnyRequiredFieldUnfilled] = useState(false);
  const [updatedFields, setUpdatedFields] = useState(null);

  useEffect(() => {
    setFormValues(values);
    setFieldsNotes({
      ...values.mediaNotes,
    });
  }, [values]);

  const handleNotifySmsHandleChange = (field, value) => {
    setNotifyViaSms({ ...notifyViaSms, [field]: value });
  };

  const getFieldOptionList = (field) => {
    return (field.additionalDetail?.optionList ?? []).map((option) => ({
      displayValue: option.responseText,
      value: option.value,
    }));
  };

  const getSelectedOptionLabel = (field) => {
    const selectedOption = (field.additionalDetail?.optionList ?? []).find(
      (option) => option.value === formValues[field.name],
    );
    return selectedOption?.responseText ?? 'select_select';
  };

  const handleValuesChange = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
    setAnyRequiredFieldUnfilled(false);
    setUpdatedFields((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleFieldsNotes = (field, value) => {
    setFieldsNotes((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const submit = async () => {
    let sendNotificationToInspector = false;

    const formData = [];
    let requiredFieldUnfilled = false;
    Object.entries(formValues).forEach(([key, value]) => {
      const foundSupervisorField = supervisorList.find((field) => field.name === key);
      if (foundSupervisorField) {
        let newObject = {
          elementGlobalId: key,
          valueField: value.toString(),
          media: values.mediaObj[key] || [],
          notesField: fieldsNotes[key] || '',
          arrayField: [],
        };
        if (foundSupervisorField.type === 'signature') {
          newObject.type = 'signature';
          newObject.elementMetaData = values.elementMetaDataMap[key];
        } else if (foundSupervisorField.type === 'response') {
          const selectedValueOption = foundSupervisorField.additionalDetail.optionList.find(
            (option) => option.value === value && value !== '',
          );
          if (selectedValueOption)
            newObject.elementMetaData = JSON.stringify({
              displayMap: { [value]: selectedValueOption.responseText },
            });
        }
        if (isShowField(foundSupervisorField)) {
          if (
            !requiredFieldUnfilled &&
            !value &&
            foundSupervisorField.properties.validationProperties.required
          ) {
            requiredFieldUnfilled = true;
          }
          formData.push(newObject);
          if (!sendNotificationToInspector && notifyViaSms[`notify_${key}`])
            sendNotificationToInspector = true;
        }
      }
    });

    if (requiredFieldUnfilled) {
      setAnyRequiredFieldUnfilled(true);
    } else {
      onSubmitForm(
        formData,
        values.submissionId,
        values.formVersionId,
        supervisorList[0].managerSectionGlobalId ?? null,
        values.mediaCustomFolderId,
        sendNotificationToInspector,
        updatedFields,
      );
    }
  };

  const isShowField = (field) => {
    const { fieldIdentitiesObj } = field;
    const { parentId, check, value } = fieldIdentitiesObj?.[field.name] || {};
    if (!parentId) return true;
    const parentValue = formValues[parentId];
    if (
      parentValue?.length > 0 &&
      ((check === 'is' && value === parentValue) || (check === 'is_not' && value !== parentValue))
    ) {
      const grandParentId = fieldIdentitiesObj[parentId]?.parentId;
      if (grandParentId) {
        const grandParentCurrentValue = formValues[grandParentId];
        if (grandParentCurrentValue?.length > 0) {
          const grandParentCheck = fieldIdentitiesObj[parentId]?.check;
          const grandParentValue = fieldIdentitiesObj[parentId]?.value;
          if (
            (grandParentCheck === 'is' && grandParentValue === grandParentCurrentValue) ||
            (grandParentCheck === 'is_not' && grandParentValue !== grandParentCurrentValue)
          ) {
            return true;
          }
        }
        return false;
      }
      return true;
    }
    return false;
  };

  const isNoteLong = (fieldName) => {
    return fieldsNotes[fieldName].length > 40;
  };

  const toggleNote = (fieldName) => {
    setExpandedNoteField((prev) => (prev === fieldName ? null : fieldName));
  };

  return (
    <AlertModal
      show={true}
      closeModal={handleClose}
      actionButtons={[]}
      noPadding
      backdropClassName={isNestedModal ? 'backdrop-overlap' : ''}
    >
      <div className='add-submission-modal py-4'>
        <div className='px-4'>
          <label className='w-100 theme-text-black-1 text-center theme-font-inter-semi-bold theme-size-1_5 mb-2 border-bottom-grey-1-h-1 pb-2'>
            {t('text_update_submission')}
          </label>
          {values.isAnyFieldMissing && (
            <label className='w-100 theme-text-red-1 text-center'>
              {t('text_field_not_contains_in_version')}
            </label>
          )}

          <div className='d-flex gap-3 align-items-center theme-font-inter-medium mt-2'>
            <img src={formIcon} alt='Form title icon' />
            {formName}
          </div>
          <div className='d-flex gap-3 align-items-center theme-font-inter-medium mt-2'>
            <img src={sectionIcon} alt='Section name icon' />
            {supervisorList[0].sectionTitle}
          </div>
        </div>

        {formValues && (
          <div className='d-flex flex-column gap-2 align-items-center'>
            <div className='fields-list-section py-2 px-4'>
              {supervisorList.map((singleField, index) => {
                const isDisabled = singleField.properties.validationProperties.readOnly ?? false;
                return isShowField(singleField) && !singleField.hidden ? (
                  <div key={index} className='field-data border-grey-1-h-1'>
                    {singleField.type === 'response' || singleField.type === 'status' ? (
                      <NestedSelect
                        options={getFieldOptionList(singleField)}
                        name={singleField.name}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        handleChange={handleValuesChange}
                        toggleClasses='p-2 border-grey-1-h-1'
                        toggleComponent={
                          <>
                            <label className='cursor-pointer'>
                              {getSelectedOptionLabel(singleField)}
                            </label>
                            <img src={chevronGreyIcon} className='rotate-180' />
                          </>
                        }
                        align={isRTL() ? 'end' : 'start'}
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'dateAndTime' ||
                      singleField.type === 'date' ||
                      singleField.type === 'time' ? (
                      <DateTimeField
                        value={
                          formValues[singleField.name]
                            ? formValues[singleField.name].toString()
                            : ''
                        }
                        name={singleField.name}
                        placeholder=''
                        type={singleField.type}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        classes='min-width-160'
                        handleChange={handleValuesChange}
                        unixTimeStamp
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'number' ? (
                      <NumberField
                        value={
                          formValues[singleField.name]
                            ? parseInt(formValues[singleField.name])
                            : undefined
                        }
                        name={singleField.name}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        classes='min-width-160'
                        handleChange={handleValuesChange}
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'ratingSlider' ? (
                      <RatingStarField
                        value={parseInt(formValues[singleField.name])}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        numOfStars={singleField.additionalDetail.noOfStars}
                        handleChange={(value) => handleValuesChange(singleField.name, value)}
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'signature' ? (
                      <SignatureField
                        name={singleField.name}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        onChange={(value) => handleValuesChange(singleField.name, value)}
                        existingSignature={values[singleField.name]}
                      />
                    ) : (
                      <Fragment>
                        <TextField
                          value={formValues[singleField.name]}
                          name={singleField.name}
                          displayName={singleField.displayName}
                          required={singleField.properties.validationProperties.required}
                          placeholder={singleField.displayName}
                          classes='mt-1'
                          handleChange={handleValuesChange}
                          disabled={isDisabled}
                        />
                        <SingleCheckBoxField
                          name={`notify_${singleField.name}`}
                          value={notifyViaSms[`notify_${singleField.name}`] ?? false}
                          handleChange={handleNotifySmsHandleChange}
                          text={<span>{t('form_field_notify_via_sms')}</span>}
                          disabled={isDisabled}
                        />
                      </Fragment>
                    )}

                    {singleField.properties.mediaProperties?.allowNotes && (
                      <>
                        {fieldsNotes[singleField.name] && (
                          <div>
                            <div className='d-flex gap-2'>
                              <strong>{t('text_note_added')}: </strong>
                              <div>
                                {isNoteLong(singleField.name) &&
                                expandedNoteField !== singleField.name ? (
                                  <>
                                    {fieldsNotes[singleField.name].slice(0, 40)}...
                                    <button
                                      onClick={() => toggleNote(singleField.name)}
                                      className='btn btn-link p-0 ms-1 theme-size-0_9'
                                    >
                                      {t('text_more').toLowerCase()}
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {fieldsNotes[singleField.name]}
                                    {isNoteLong(singleField.name) && (
                                      <button
                                        onClick={() => toggleNote(singleField.name)}
                                        className='btn btn-link p-0 ms-1 theme-size-0_9'
                                      >
                                        {t('text_hide').toLowerCase()}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        <div
                          className={`d-flex align-items-center gap-4 ${
                            isDisabled ? 'disabled' : ''
                          }`}
                        >
                          <div className='d-flex align-items-center gap-2'>
                            <label className='theme-text-black-1'>{t('text_add')} :</label>
                            <div className='vertical-line'></div>
                          </div>
                          <div
                            className='d-flex align-items-center gap-2 cursor-pointer'
                            onClick={() => {
                              setOpenedNotesModal({
                                fieldName: singleField.name,
                                fieldValue: fieldsNotes[singleField.name] || '',
                              });
                            }}
                          >
                            <img src={filePlusIcon} />
                            <label className='theme-text-black-1 cursor-pointer'>
                              {t('text_note')}
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : null;
              })}
            </div>

            {openedNotesModal && (
              <NotesModal
                openedNotesModal={openedNotesModal}
                setOpenedNotesModal={setOpenedNotesModal}
                handleFieldsNotes={handleFieldsNotes}
              />
            )}
            {anyRequiredFieldUnfilled && (
              <span className='align-self-start theme-text-red-1 px-4'>
                {t('error_all_required_fields_must_be_filled')}
              </span>
            )}
            <div className='w-100 d-flex gap-3 px-4'>
              <FormButton
                classes='w-100'
                text='button_cancel'
                variant='white-1'
                onClick={handleClose}
              />
              <FormLoadingButton
                classes='w-100'
                onClick={submit}
                loading={loading}
                text='button_submit'
                variant='green-1'
                disabled={supervisorList.length === 0}
              />
            </div>
          </div>
        )}
      </div>
    </AlertModal>
  );
};

AddSubmission.propTypes = {
  handleClose: PropTypes.func,
  onSubmitForm: PropTypes.func.isRequired,
  supervisorList: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  isNestedModal: PropTypes.bool,
  formName: PropTypes.string.isRequired,
};

export default AddSubmission;

const NotesModal = ({ openedNotesModal, setOpenedNotesModal, handleFieldsNotes }) => {
  const [fieldNote, setFieldNote] = useState('');
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const maxCharacterLimit = 500;
  const handleNote = (_, value) => {
    let text = value;
    const isExceedingLimit = text.length > maxCharacterLimit;
    if (isExceedingLimit) {
      text = value.slice(0, maxCharacterLimit);
    }
    setError(isExceedingLimit ? 'max character limit' : null);
    setFieldNote(text);
  };

  useEffect(() => {
    if (openedNotesModal.fieldValue) {
      setFieldNote(openedNotesModal.fieldValue);
    }
  }, [openedNotesModal.fieldValue]);

  return (
    <AlertModal show={!!openedNotesModal} actionButtons={[]} backdropClassName={'backdrop-overlap'}>
      <>
        <label className='theme-text-black-1 theme-font-inter-semi-bold theme-size-1_5'>
          {t('text_add_note')}
        </label>
        <TextArea
          name={openedNotesModal.fieldName}
          value={fieldNote}
          placeholder='text_enter_note_here'
          classes='w-100 mb-3'
          handleChange={handleNote}
          rows={4}
          error={error}
        />
        <div className='d-flex w-100 gap-2 justify-content-center'>
          <FormButton
            text={t('button_cancel')}
            variant='white-1'
            onClick={() => setOpenedNotesModal(null)}
          />
          <FormLoadingButton
            text='button_save'
            variant='green-1'
            onClick={() => {
              handleFieldsNotes(openedNotesModal.fieldName, fieldNote);
              setOpenedNotesModal(null);
            }}
            loading={false}
          />
        </div>
      </>
    </AlertModal>
  );
};

NotesModal.propTypes = {
  openedNotesModal: PropTypes.object.isRequired,
  setOpenedNotesModal: PropTypes.func.isRequired,
  handleFieldsNotes: PropTypes.func.isRequired,
};
